import type { VisibleRangeProfileBucket } from '@/features/chart/lib/visible-range-profile'

export type ProjectedBucket = { topPercent: number; heightPercent: number }

/**
 * Maps buckets onto a vertical axis spanning [visibleMin, visibleMax], clipping any bucket that
 * only partially overlaps it and dropping ones entirely outside. CSS `top` is 0% at the HIGHEST
 * price, not the lowest, so the ratio is inverted before being returned.
 */
export function createPriceRangeProjector(
  visibleMin: number,
  visibleMax: number
): (bucket: VisibleRangeProfileBucket) => ProjectedBucket | null {
  if (!(visibleMax > visibleMin)) return () => null

  return (bucket) => {
    const clippedLow = Math.max(bucket.low, visibleMin)
    const clippedHigh = Math.min(bucket.high, visibleMax)
    if (clippedHigh <= clippedLow) return null

    const lowRatio = (clippedLow - visibleMin) / (visibleMax - visibleMin)
    const highRatio = (clippedHigh - visibleMin) / (visibleMax - visibleMin)
    return { topPercent: 100 - highRatio * 100, heightPercent: (highRatio - lowRatio) * 100 }
  }
}

/**
 * For a profile whose OWN request bounds already are the axis to draw against (the
 * Volume/Liquidation/OI visible-range family — its request sends the chart's current visible
 * price range as priceMin/priceMax, so the whole returned grid already spans it end-to-end).
 * Equivalent to today's equal-row spacing: nothing clips, since the buckets' own span IS the
 * visible range.
 */
export function createFullBucketRangeProjector(
  buckets: readonly VisibleRangeProfileBucket[]
): (bucket: VisibleRangeProfileBucket) => ProjectedBucket | null {
  const visibleMin = buckets[0]?.low
  const visibleMax = buckets[buckets.length - 1]?.high
  if (visibleMin === undefined || visibleMax === undefined) return () => null
  return createPriceRangeProjector(visibleMin, visibleMax)
}
